import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { ArrowClockwise, DownloadSimple } from "@phosphor-icons/react";
import useBranches from "@/hooks/useBranches";
import useBranchTypes from "@/hooks/useBranchTypes";
import { MODEL_CATEGORY_OPTIONS } from "@/components/PackageFormDrawer";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function startOfMonthISO() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

const NUMERIC_COLS = [
  { key: "dealer_margin", label: "Dealer Margin" },
  { key: "mandatory_margin", label: "Mandatory" },
  { key: "helmet_margin", label: "Helmet" },
  { key: "add_acc_margin", label: "Add. Acc." },
  { key: "excess_paid", label: "Excess Paid" },
  { key: "write_off", label: "Write-off" },
  { key: "insurance_paid", label: "Ins. Paid" },
  { key: "insurance_charged", label: "Ins. Charged" },
  { key: "insurance_profit", label: "Ins. Profit" },
  { key: "financier_commission", label: "Fin. Commission" },
];

function toCsv(rows, totals) {
  const cols = [
    "sale_date", "channel", "vin", "engine_no", "package_name", "customer_name", "customer_phone",
    "branch_name", "agent_name", "financier_name", "sale_price",
    ...NUMERIC_COLS.map((c) => c.key), "total_profit",
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [cols.join(",")];
  rows.forEach((r) => lines.push(cols.map((c) => escape(r[c])).join(",")));
  const totalsRow = cols.map((c) => {
    if (c === "sale_date") return "TOTALS";
    return totals[c] !== undefined ? escape(totals[c]) : "";
  });
  lines.push(totalsRow.join(","));
  return lines.join("\n");
}

export default function ProfitPerVehicleReportPage() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(startOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [branchId, setBranchId] = useState("");
  const [branchTypeId, setBranchTypeId] = useState("");
  const [modelCategory, setModelCategory] = useState("");
  const [packageId, setPackageId] = useState("");
  const [packages, setPackages] = useState([]);
  const { branches } = useBranches({ activeOnly: true, includeAll: true });
  const { types: branchTypes } = useBranchTypes({ activeOnly: true });

  // Branch dropdown narrows to the picked branch type (same behaviour as
  // the Branch KPIs report).
  const visibleBranches = useMemo(
    () => branches.filter((b) => !branchTypeId || b.branch_type_id === branchTypeId),
    [branches, branchTypeId]
  );

  useEffect(() => {
    api
      .get("/packages", { params: { active_only: true } })
      .then((r) => setPackages(r.data || []))
      .catch(() => setPackages([]));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (branchId) params.branch_id = branchId;
      if (branchTypeId) params.branch_type_id = branchTypeId;
      if (modelCategory) params.model_category = modelCategory;
      if (packageId) params.package_id = packageId;
      const { data } = await api.get("/reports/profit-per-vehicle", { params });
      setRows(data.rows || []);
      setTotals(data.totals || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCsv = () => {
    const csv = toCsv(rows, totals);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `profit-per-vehicle_${dateFrom}_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summaryStrip = useMemo(() => {
    return [
      { label: "Sales", value: rows.length.toString(), muted: true },
      { label: "Revenue", value: fmtMoney(totals.sale_price || 0) },
      { label: "Total Profit", value: fmtMoney(totals.total_profit || 0), accent: true },
      {
        label: "Margin %",
        value: totals.sale_price
          ? `${((totals.total_profit / totals.sale_price) * 100).toFixed(2)}%`
          : "—",
      },
    ];
  }, [rows.length, totals]);

  return (
    <div className="space-y-6" data-testid="profit-per-vehicle-page">
      <PageHeader
        eyebrow="Owner P&L"
        title="Profit Per Vehicle"
        description="Dealer margins snapshotted at sale time. Reports going forward — edits to a package do not affect past sales."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ppv-summary-strip">
        {summaryStrip.map((s) => (
          <div
            key={s.label}
            className={`border ${
              s.accent ? "border-amber-500/50 bg-amber-50/40 dark:bg-amber-900/10" : "border-canvas-line bg-canvas-card"
            } p-3`}
          >
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {s.label}
            </div>
            <div className={`text-lg font-semibold ${s.muted ? "text-muted-foreground" : ""}`}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-canvas-card border border-canvas-line p-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            From
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="ppv-date-from"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            To
          </span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="ppv-date-to"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 min-w-[160px]">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Branch Type
          </span>
          <select
            value={branchTypeId}
            onChange={(e) => {
              setBranchTypeId(e.target.value);
              // Drop a branch pick that no longer belongs to the type.
              if (branchId && e.target.value) {
                const b = branches.find((x) => x.id === branchId);
                if (b && b.branch_type_id !== e.target.value) setBranchId("");
              }
            }}
            data-testid="ppv-branch-type"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm"
          >
            <option value="">All types</option>
            {branchTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 min-w-[180px]">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Branch
          </span>
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            data-testid="ppv-branch"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm"
          >
            <option value="">All branches</option>
            {visibleBranches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 min-w-[150px]">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Model
          </span>
          <select
            value={modelCategory}
            onChange={(e) => setModelCategory(e.target.value)}
            data-testid="ppv-model"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm"
          >
            <option value="">All models</option>
            {MODEL_CATEGORY_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 min-w-[220px]">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Package
          </span>
          <select
            value={packageId}
            onChange={(e) => setPackageId(e.target.value)}
            data-testid="ppv-package"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm"
          >
            <option value="">All packages</option>
            {packages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={load}
          disabled={loading}
          data-testid="ppv-apply"
          className="px-4 py-2 bg-ink text-canvas text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
        >
          <ArrowClockwise size={14} weight="bold" />
          {loading ? "Loading…" : "Apply"}
        </button>
        <button
          onClick={downloadCsv}
          disabled={!rows.length}
          data-testid="ppv-download"
          className="px-4 py-2 border border-canvas-line text-sm hover:border-ink disabled:opacity-50 flex items-center gap-2"
        >
          <DownloadSimple size={14} />
          CSV
        </button>
      </div>

      <div className="bg-canvas-card border border-canvas-line overflow-x-auto">
        <table className="w-full text-xs" data-testid="ppv-table">
          <thead className="border-b border-canvas-line text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Channel</th>
              <th className="text-left px-3 py-2">VIN</th>
              <th className="text-left px-3 py-2">Package</th>
              <th className="text-left px-3 py-2">Customer / Sub-Dealer</th>
              <th className="text-left px-3 py-2">Branch</th>
              <th className="text-left px-3 py-2">Financier</th>
              <th className="text-right px-3 py-2">Sale ₹</th>
              {NUMERIC_COLS.map((c) => (
                <th key={c.key} className="text-right px-3 py-2 whitespace-nowrap">
                  {c.label}
                </th>
              ))}
              <th className="text-right px-3 py-2 font-semibold text-amber-700 dark:text-amber-400">
                Total Profit
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-canvas-line">
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={10 + NUMERIC_COLS.length}
                  className="px-3 py-8 text-center text-muted-foreground"
                >
                  {loading ? "Loading…" : "No sales in this period."}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.sale_id} data-testid={`ppv-row-${r.sale_id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{r.sale_date}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span
                    className={`px-1.5 py-0.5 text-[9px] uppercase tracking-widest border ${
                      r.channel === "SUB-DEALER"
                        ? "border-indigo-500/50 text-indigo-700 dark:text-indigo-300"
                        : "border-canvas-line text-muted-foreground"
                    }`}
                  >
                    {r.channel === "SUB-DEALER" ? "AD" : "Retail"}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap font-mono">{r.vin}</td>
                <td className="px-3 py-2">{r.package_name}</td>
                <td className="px-3 py-2">{r.customer_name}</td>
                <td className="px-3 py-2">{r.branch_name}</td>
                <td className="px-3 py-2">{r.financier_name || "—"}</td>
                <td className="px-3 py-2 text-right">{fmtMoney(r.sale_price)}</td>
                {NUMERIC_COLS.map((c) => (
                  <td key={c.key} className="px-3 py-2 text-right">
                    {fmtMoney(r[c.key])}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-semibold text-amber-700 dark:text-amber-400">
                  {fmtMoney(r.total_profit)}
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="border-t border-canvas-line font-semibold">
              <tr>
                <td colSpan={7} className="px-3 py-2 text-right">
                  Totals
                </td>
                <td className="px-3 py-2 text-right">{fmtMoney(totals.sale_price)}</td>
                {NUMERIC_COLS.map((c) => (
                  <td key={c.key} className="px-3 py-2 text-right">
                    {fmtMoney(totals[c.key])}
                  </td>
                ))}
                <td className="px-3 py-2 text-right text-amber-700 dark:text-amber-400">
                  {fmtMoney(totals.total_profit)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
